/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { getVouchers } from '~src/services/api/contracts';
import { getOfferVouchers } from '~src/services/api/offers';
import { OffersResultsView } from './OffersResultsView';
import {
  EvaluationVoucherResponseLike,
  ResultContractLike,
  ResultOfferLike,
} from './resultsData';

type OffersResultsContractViewProps = {
  projectId: number;
  tenderId: number;
  contract: ResultContractLike;
  offers: ResultOfferLike[];
  doSetWinner?: (params: any) => any;
  doRemoveWinner?: (params: any) => any;
};

export const OffersResultsContractView: FC<OffersResultsContractViewProps> = ({
  projectId,
  tenderId,
  contract,
  offers,
  doSetWinner,
  doRemoveWinner,
}) => {
  const intl = useIntl();

  const [voucherResponsesByOfferId, setVoucherResponsesByOfferId] = useState<
    Record<number, EvaluationVoucherResponseLike[]>
  >({});

  const [isResponsesLoading, setIsResponsesLoading] = useState(false);

  const contractOffers = useMemo(
    () => offers.filter((offer) => offer.contract_id === contract.id),
    [offers, contract.id]
  );

  const {
    data: vouchers = [],
    isLoading: isVouchersLoading,
    isError: isVouchersError,
  } = useQuery<any[]>(
    ['result-contract-vouchers', projectId, tenderId, contract.id],
    () =>
      getVouchers({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contract.id),
      }),
    {
      enabled: !!projectId && !!tenderId && !!contract.id,
    }
  );

  useEffect(() => {
    let isMounted = true;

    const loadResponses = async () => {
      if (!projectId || !tenderId || !contractOffers.length) {
        setVoucherResponsesByOfferId({});
        return;
      }

      setIsResponsesLoading(true);

      try {
        const entries = await Promise.all(
          contractOffers.map(async (offer) => {
            try {
              const responses = await getOfferVouchers({
                projectId,
                tenderId,
                offerId: offer.id,
              });

              return [offer.id, responses || []] as [
                number,
                EvaluationVoucherResponseLike[]
              ];
            } catch (error) {
              return [offer.id, []] as [
                number,
                EvaluationVoucherResponseLike[]
              ];
            }
          })
        );

        if (!isMounted) return;

        setVoucherResponsesByOfferId(
          entries.reduce<Record<number, EvaluationVoucherResponseLike[]>>(
            (acc, [offerId, responses]) => {
              acc[offerId] = responses;
              return acc;
            },
            {}
          )
        );
      } finally {
        if (isMounted) {
          setIsResponsesLoading(false);
        }
      }
    };

    loadResponses();

    return () => {
      isMounted = false;
    };
  }, [projectId, tenderId, contractOffers]);

  if (isVouchersLoading || isResponsesLoading) {
    return <Spin />;
  }

  if (isVouchersError) {
    return (
      <Alert
        type="warning"
        showIcon
        message={intl.formatMessage({
          id: 'results-contract-vouchers-load-error-message',
        })}
        description={intl.formatMessage({
          id: 'results-contract-vouchers-load-error-description',
        })}
      />
    );
  }

  return (
    <OffersResultsView
      projectId={projectId}
      tenderId={tenderId}
      contract={contract}
      offers={offers}
      vouchers={vouchers}
      voucherResponsesByOfferId={voucherResponsesByOfferId}
      doSetWinner={doSetWinner}
      doRemoveWinner={doRemoveWinner}
    />
  );
};
